import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import { selectActors, selectAllPropPresets, updateSelectedTake } from "../../app/slices/TrainingSlice";
import { selectUsedProps } from "../../app/slices/PropLibrarySlice";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import PiSelect, { PiSelectOption } from "../../components/PiSelect";
import { getTakeStartProps, getTakeStartPropCandidates, getTakeStartPropLabel, getTakeStartingViewErrors, withTakeStartingProp } from "../../helpers/TakeStartingView";
import Take3D from "../../models/Take3D";
import { useTranslation } from "react-i18next";

export default function TakeStartingViewField({ take }: { take: Take3D }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const positions = useSelector(selectEnvironmentInteractionPoints) ?? [];
  const presets = useSelector(selectAllPropPresets) ?? [];
  const actors = useSelector(selectActors) ?? [];
  const usedProps = useSelector(selectUsedProps) ?? {};
  const candidates = getTakeStartProps(take, positions, presets);
  const eligible = getTakeStartPropCandidates(take, positions, presets, usedProps).map(({ prop }) => prop);
  const selection = take.startPropInspect ?? { propId: "", tag: "" };
  const selectedProp = presets.find((p) => p.id === selection.propId);
  const definitions = selectedProp ? usedProps[selectedProp.propId]?.metadata?.inspectDefinitions ?? [] : [];
  const missingMetadata = candidates.some((p) => !usedProps[p.propId]?.metadata);
  const errors = getTakeStartingViewErrors(take, positions, presets, usedProps);
  const update = (changes: Partial<Take3D>) => dispatch(updateSelectedTake({ ...take, ...changes } as Take3D));
  const unavailable = String(t("unavailable", "Unavailable"));
  const propOptions: PiSelectOption[] = [
    ...(selection.propId && !eligible.some((p) => p.id === selection.propId)
      ? [{ value: selection.propId, label: `${selectedProp?.name ?? selection.propId} (${unavailable})` }] : []),
    ...eligible.map((p) => ({ value: p.id, label: getTakeStartPropLabel(p, take.attachments?.[p.id], actors) })),
  ];
  const inspectOptions: PiSelectOption[] = [
    ...(selection.tag && !definitions.some((d) => d.name === selection.tag)
      ? [{ value: selection.tag, label: `${selection.tag} (${unavailable})` }] : []),
    ...Array.from(new Set(definitions.map((d) => d.name))).map((tag) => ({ value: tag, label: tag.replace(/_/g, " ") })),
  ];
  return <div className="flex flex-col gap-[8px]" data-testid="take-starting-view">
    <div className="font-medium text-[14px]">{t("take_start_with", "Start with")}</div>
    <PiSegmentedToggle<"camera" | "propInspect">
      ariaLabel={String(t("take_start_with", "Start with"))} dense fullWidth variant="square"
      value={take.startViewMode ?? "camera"}
      options={[
        { value: "camera", label: t("camera", "Camera") },
        { value: "propInspect", label: t("take_start_prop_inspect", "Prop inspect") },
      ]}
      onChange={(startViewMode) => update({ startViewMode })}
    />
    {take.startViewMode === "propInspect" && <>
      <PiSelect label={String(t("prop", "Prop"))} value={selection.propId} options={propOptions}
        placeholder={String(t("take_start_select_prop", "Select a Prop"))} noneLabel={String(t("take_start_none", "None"))}
        onChange={(propId) => {
          const updated = withTakeStartingProp(take, propId);
          if (updated !== take) dispatch(updateSelectedTake(updated));
        }} />
      <PiSelect label={String(t("take_start_inspect_view", "Inspect view"))} value={selection.tag} options={inspectOptions}
        placeholder={String(t("take_start_select_inspect", "Select an Inspect view"))} noneLabel={String(t("take_start_none", "None"))}
        disabled={!definitions.length}
        onChange={(tag) => update({ startPropInspect: { ...selection, tag } })} />
      {!eligible.length && <p className="text-[12px] text-creator-text-sub">{missingMetadata
        ? t("take_start_metadata_unavailable", "Prop metadata is unavailable. Reload the training to try again.")
        : t("take_start_no_props", "No Props with inspect views are placed at this Take location or attached to actors here.")}</p>}
      {errors.map((error) => <p key={error} role="alert" className="text-[12px] text-creator-error">{error}</p>)}
    </>}
  </div>;
}
